// Safety numbers — docs/03-security.md §4.2.
//
// Both apps derive SHA-256 over the two identity public keys sorted
// byte-lexicographically (so both sides compute identical bytes), rendered as
// 6 emoji + 8 digits. Users read it aloud in-call; a MITM (who must present
// different keys to each side) cannot make them match.

import { sha256 as nobleSha256 } from '@noble/hashes/sha2.js';
import { base64ToBytes, concatBytes } from '../bytes.js';

/**
 * 64-entry emoji alphabet (6 bits per symbol). Chosen for visual mutual
 * distinctness and cross-platform availability; order is part of the protocol
 * between two Merkury clients — do not reorder.
 */
const EMOJI = [
  '🐶', '🐱', '🦁', '🐴', '🦄', '🐮', '🐷', '🐭',
  '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🐸',
  '🐔', '🐧', '🐦', '🦅', '🦉', '🦆', '🐢', '🐍',
  '🐳', '🐬', '🐟', '🐙', '🦀', '🐝', '🦋', '🐞',
  '🌵', '🌲', '🍀', '🌻', '🌹', '🍄', '🌍', '🌙',
  '⭐', '⚡', '🔥', '🌈', '☂️', '❄️', '🍎', '🍋',
  '🍉', '🍇', '🍓', '🍒', '🥕', '🌽', '🍞', '🧀',
  '🚗', '✈️', '🚀', '⛵', '🔑', '🎈', '🎁', '🎵',
] as const;

export interface SafetyNumber {
  emoji: string[]; // 6 symbols
  digits: string; // 8 decimal digits
}

/**
 * Derive the safety number for a pair of identity keys (raw 32-byte Ed25519
 * keys, base64). Symmetric: `derive(a, b)` === `derive(b, a)`.
 */
export async function deriveSafetyNumber(
  publicKeyAB64: string,
  publicKeyBB64: string,
): Promise<SafetyNumber> {
  const a = base64ToBytes(publicKeyAB64);
  const b = base64ToBytes(publicKeyBB64);
  if (!a || !b) throw new Error('invalid public key material');
  const [first, second] = compareBytes(a, b) <= 0 ? [a, b] : [b, a];
  const digest = await sha256(concatBytes(first, second));

  // Bytes 0–5 → emoji (each mod 64 = full byte entropy folded to 6 bits);
  // bytes 6–9 → 8 digits from the big-endian uint32 mod 10^8, zero-padded.
  const emoji: string[] = [];
  for (let i = 0; i < 6; i++) emoji.push(EMOJI[digest[i]! % 64]!);
  const n =
    ((digest[6]! << 24) | (digest[7]! << 16) | (digest[8]! << 8) | digest[9]!) >>> 0;
  const digits = String(n % 100_000_000).padStart(8, '0');
  return { emoji, digits };
}

/** Human-readable one-line rendering, e.g. "🐶 🦄 🌙 ⭐ 🍒 🔑 · 4821 0937". */
// Digits only, grouped in 4s ("4821 0937"), matching the Android UI
// (SafetyNumberPanel). The emoji row was dropped from the product on both
// clients — the spoken comparison is the 8 digits. `sn.emoji` is still derived
// (independent bytes) but no longer shown.
export function formatSafetyNumber(sn: SafetyNumber): string {
  return `${sn.digits.slice(0, 4)} ${sn.digits.slice(4)}`;
}

function compareBytes(a: Uint8Array, b: Uint8Array): number {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i]! !== b[i]!) return a[i]! - b[i]!;
  }
  return a.length - b.length;
}

// SHA-256 with zero browser globals: WebCrypto when present (browser/Node —
// identical output), else the pure-JS @noble implementation (headless JSC has
// no `crypto.subtle`). Same WebCrypto-preferred idiom as ed25519.ts.
async function sha256(data: Uint8Array): Promise<Uint8Array> {
  const subtle = globalThis.crypto?.subtle;
  if (subtle) {
    const copy = new Uint8Array(new ArrayBuffer(data.length));
    copy.set(data);
    return new Uint8Array(await subtle.digest('SHA-256', copy));
  }
  return nobleSha256(data);
}
